import React, { useEffect, useState } from "react";
import { Row, Col, Spinner, Card, CardHeader, CardBody } from "reactstrap";
import salesMembershipService from "../../services/api/sales-membership-service.";

const CellValue = ({ value, loading, fixed = 2, currency }) => {
  if (loading) return <Spinner size="sm" />;
  const num = Number(value || 0);
  const formatted = num.toFixed(fixed);
  if (currency === 'INR') return `₹ ${formatted}`;
  if (currency === 'USD') return `$ ${formatted}`;
  return formatted;
};

const MiniTable = ({ title, rows, loading }) => (
  <Card className="h-90 shadow-sm">
    <CardHeader className="bg-light border-bottom">
      <h6 className="mb-2 text-muted" style={{ fontSize: "0.8rem" }}>{title}</h6>
    </CardHeader>
    <CardBody className="p-0">
      <div className="border rounded">
        {rows.map((r, i) => (
          <Row key={i} className={`g-0 ${i !== 0 ? 'border-top' : ''}`}>
            <Col xs="8" className="p-2 bg-light">
              <div className="fw-semibold text-dark" style={{ fontSize: '0.8rem' }}>{r.label}</div>
            </Col>
            <Col xs="4" className="p-2">
              <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                <CellValue value={r.value} loading={loading} fixed={r.fixed} currency={r.currency} />
              </div>
            </Col>
          </Row>
        ))}
      </div>
    </CardBody>
  </Card>
);

const SummaryThreeTables = ({ formState }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    totalSales: 0,
    activeSubscribers: 0,
    originalTotal: 0,
    originalInr: 0,
    inrConvertedOfOrgUsd: 0,
    originalUsd: 0,
    publisherShare: 0,
    mediologyShare: 0,
    digitalAndEpaperTotal: 0,
    digitalAndEpaperInr: 0,
    digitalAndEpaperlUsd: 0,
    inrConvertedOfDigitalAndEpaperUsd: 0,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [resSales, resActive, resOrigTotal, resOrigInr, resInrConvertedOfOrgUsd, resOrigUsd, resPub, resMed, resDigitalAndEpaperTotal, resDigitalAndEpaperInr, resDigitalAndEpaperlUsd, resInrConvertedOfDigitalAndEpaperUsd] = await Promise.all([
          salesMembershipService.getTotalSales({ ...formState, currency: '' }),
          salesMembershipService.getActiveSubscribers(formState),
          salesMembershipService.getTotalTxnAmountByCurrency({ ...formState, currency: '', amount_type: 'inr_converted' }),
          salesMembershipService.getTotalTxnAmountByCurrency({ ...formState, currency: 'INR',amount_type: 'inr_converted' }),
          salesMembershipService.getTotalTxnAmountByCurrency({ ...formState, currency: 'USD', amount_type: 'inr_converted' }),
          salesMembershipService.getTotalTxnAmountByCurrency({ ...formState, currency: 'USD' }),
          salesMembershipService.getShareAmount({ ...formState, type: 'publisher' }),
          salesMembershipService.getShareAmount({ ...formState, type: 'mediology' }),
          salesMembershipService.getTotalTxnAmount({ ...formState, currency: ''}),
          salesMembershipService.getTotalTxnAmount({ ...formState, currency: 'INR'}),
          salesMembershipService.getTotalTxnAmount({ ...formState, currency: 'USD'}),
          salesMembershipService.getTotalTxnAmount({ ...formState, currency: 'USD', amount_type: 'inr'  }),
        ]);

        if (cancelled) return;

        setData({
          totalSales: resSales?.status ? (resSales.data || 0) : 0,
          activeSubscribers: resActive?.status
            ? (typeof resActive.data?.count === 'number' ? resActive.data.count : (resActive.count || 0))
            : 0,
          originalTotal: resOrigTotal?.status ? (resOrigTotal.data || 0) : 0,
          originalInr: resOrigInr?.status ? (resOrigInr.data || 0) : 0,
          inrConvertedOfOrgUsd: resInrConvertedOfOrgUsd?.status ? (resInrConvertedOfOrgUsd.data || 0) : 0,
          originalUsd: resOrigUsd?.status ? (resOrigUsd.data || 0) : 0,
          publisherShare: resPub?.status ? (resPub.data?.amount || 0) : 0,
          mediologyShare: resMed?.status ? (resMed.data?.amount || 0) : 0,
          digitalAndEpaperTotal: resDigitalAndEpaperTotal?.status ? (resDigitalAndEpaperTotal.data || 0) : 0,
          digitalAndEpaperInr: resDigitalAndEpaperInr?.status ? (resDigitalAndEpaperInr.data || 0) : 0,
          digitalAndEpaperlUsd: resDigitalAndEpaperlUsd?.status ? (resDigitalAndEpaperlUsd.data || 0) : 0,
          inrConvertedOfDigitalAndEpaperUsd: resInrConvertedOfDigitalAndEpaperUsd?.status ? (resInrConvertedOfDigitalAndEpaperUsd.data || 0) : 0,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [formState]);

  return (
    <div className="mb-2">
      <Row>
        <Col md="4" sm="12" xs="12">
          <MiniTable
            title="Sales Amounts (Digital)"
            loading={loading}
            rows={[
              { label: 'Total Sales Amount (INR + USD)', value: data.originalTotal, fixed: 2, currency: 'INR' },
              { label: 'Sales Amount (INR)', value: data.originalInr, fixed: 2, currency: 'INR' },
              { label: 'Sales Amount (USD)', value: data.originalUsd, fixed: 2, currency: 'USD' },
              { label: 'Sales Amount (USD converted to INR)', value: data.inrConvertedOfOrgUsd, fixed: 2, currency: 'INR' },
            ]}
          />
        </Col>
        <Col md="4" sm="12" xs="12">
          <MiniTable
            title="Sales Amounts (Digital + Epaper)"
            loading={loading}
            rows={[
              { label: 'Total Sales Amount (INR + USD)', value: data.digitalAndEpaperTotal, fixed: 2, currency: 'INR' },
              { label: 'Sales Amount (INR)', value: data.digitalAndEpaperInr, fixed: 2, currency: 'INR' },
              { label: 'Sales Amount (USD)', value: data.digitalAndEpaperlUsd, fixed: 2, currency: 'USD' },
              { label: 'Sales Amount (USD converted to INR)', value: data.inrConvertedOfDigitalAndEpaperUsd, fixed: 2, currency: 'INR' },
            ]}
          />
        </Col>
        <Col md="4" sm="12" xs="12">
          <MiniTable
            title="Sales Count and Shares"
            loading={loading}
            rows={[
              { label: 'Total Sales', value: data.totalSales, fixed: 0 },
              { label: 'Active Subscribers', value: data.activeSubscribers, fixed: 0 },
              { label: 'Publisher Share (INR)', value: data.publisherShare, fixed: 2, currency: 'INR' },
              { label: 'Mediology Share (INR)', value: data.mediologyShare, fixed: 2, currency: 'INR' },
            ]}
          />
        </Col>
      </Row>
    </div>
  );
};

export default SummaryThreeTables;

